import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Alert, Dialog, Small, TextArea } from "@fronterahealth/frontera-ui-components";

import { importTemplateJson } from "@api/rest/types-and-hooks";
import { notifyError, notifySuccess } from "@components/notifications/notifications";

interface ImportTemplateDialogProps {
  isDialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
}

export const ImportTemplateDialog: React.FC<ImportTemplateDialogProps> = ({ isDialogOpen, setDialogOpen }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [jsonInput, setJsonInput] = useState("");

  const importMutation = useMutation({
    mutationFn: () =>
      importTemplateJson({
        json_data: jsonInput,
        organization_ids: [],
      }),
    onSuccess: (result) => {
      if (result.status !== 201) {
        notifyError("Failed to import template");
        return;
      }
      notifySuccess("Template imported successfully");
      void queryClient.invalidateQueries({ queryKey: ["templates"] });
      setDialogOpen(false);
      void navigate(`/templates/${result.data.id}`);
    },
    onError: () => notifyError("Failed to import template"),
  });

  useEffect(() => {
    if (!isDialogOpen) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setJsonInput("");
    }
  }, [isDialogOpen]);

  return (
    <Dialog
      title="Import Template from JSON"
      open={isDialogOpen}
      setOpen={setDialogOpen}
      primaryButton={{
        text: "Import",
        onClick: () => importMutation.mutate(),
        disabled: importMutation.isPending || !jsonInput.trim(),
      }}
      secondaryButton={{
        text: "Cancel",
        onClick: () => setDialogOpen(false),
      }}
      overflowBehavior="show"
      size="lg"
    >
      <div className="mb-4">
        <Alert
          title=""
          appearance="neutral"
          text={
            <p className="-mt-2">
              Open an existing published template and copy the Export Template JSON from the sidebar. Insert that copied
              text here to create a copy of that template.
            </p>
          }
        />
      </div>

      <div className="flex flex-col gap-4 mt-4">
        <div className="flex flex-col gap-2">
          <Small>Paste Template JSON</Small>
          <TextArea
            id="template-json"
            label=""
            value={jsonInput}
            onChange={(e) => setJsonInput(e.target.value)}
            placeholder='{"name": "My Template", "description": "Example"}'
            rows={10}
            className="w-full"
          />
        </div>
      </div>
    </Dialog>
  );
};
